import type { RogueEngine, RogueEngineOptions, RackBuilder } from '@rogue/rackbuilder'
import { onMounted, onBeforeUnmount, type Ref } from 'vue'
import type { RackbuilderConfig } from './RackbuilderComposable'
import { useRogue } from '@/js/services/Rogue'
import type { IRogue } from '@/js/services/RogueTypes'

type EngineConfig = {
  key?: string
  container?: Ref<HTMLDivElement | undefined>,
  engineOptions?: Partial<RogueEngineOptions>,
  autoInit?: boolean,
}

const DEFAULT_ENGINE_CONFIG: RogueEngineOptions = {
  interactive           : true,
  envMap                : 'https://assets.roguefitness.com/raw/upload/v1714576324/glb/hdri256.hdr',
  initialCameraPosition : [-2.45, 1, 2.75],
  orbitTarget           : [0, 1.2, 0],
  useShadows            : false,
  controlsOptions       : {
    enablePan    : false,
    enableRotate : true,
    enableZoom   : true,
  },
}

export const PDP_ENGINE_KEY = 'pdp'

export class Rogue3DEngine {
  private initPromise: Promise<void> | undefined

  private onInitialized: ((e: typeof import('@rogue/rackbuilder')) => unknown)[] = []

  engine: RogueEngine | null = null

  public builders: RackBuilder[] = []

  module: typeof import('@rogue/rackbuilder') | undefined

  constructor(private config:EngineConfig, private rogue: IRogue) {
    this.config = config
    this.rogue = rogue
  }

  init(): Promise<void> {
    // The Engine Config controls the cameras, controls, etc
    // It is separate from the Rackbuilder, since we can have more than one rackbuilder in the future for a single engine.
    // For example if we want to replace Zeus Gym builder with Rackbuilder
    const engineConfig = Object.assign({}, DEFAULT_ENGINE_CONFIG, this.config.engineOptions || {}, {}) as RogueEngineOptions

    if (!this.initPromise) {
      if (import.meta.env.SSR) {
        this.initPromise = Promise.resolve()
      } else {
        this.initPromise = new Promise((resolve, reject) => import('@rogue/rackbuilder').then(mod => {
          this.rogue.log.info('Engine created')

          this.module = mod
          const engineObj = new mod.RogueEngine(engineConfig)
          this.engine = engineObj

          const container = this.config.container?.value
          if (container) {
            engineObj.setContainer(container)
          }

          engineObj.start()

          this.onInitialized.forEach(fn => fn(mod))
          this.onInitialized = []

          resolve()
        })
          .catch(reject) // surface import failures to callers instead of hanging forever
        )
      }
    }

    return Promise.resolve(this.initPromise)
  }

  onInit(fn: () => unknown) {
    if (this.engine) {
      fn()
    } else {
      this.onInitialized.push(fn)
    }
  }

  dispose() {
    this.onInitialized = []
    this.builders.forEach(b => b.dispose())
    this.builders = []
    this.engine?.dispose()
  }
}

const CACHE: {[key: string] : Rogue3DEngine} = {}

export function use3DEngine(config: EngineConfig, builders: RackbuilderConfig[] = []) {
  const rogue = useRogue()
  const engine = new Rogue3DEngine(config, rogue)

  if (config.key) {
    if (CACHE[config.key]) {
      CACHE[config.key].dispose() // dispose before overwrite to prevent WebGL context leak

      rogue.log.debug(`Adding engine to cache ${config.key}, but already has a cached value`)
    }
    CACHE[config.key] = engine
  }

  onMounted(() => {
    if (config.autoInit) {
      engine.init().catch(err => rogue.log.error('Error loading Rackbuilder', err))
    }
  })

  onBeforeUnmount(() => {
    engine.dispose()
    if (config.key) {
      delete CACHE[config.key]
    }
  })

  return {
    engine,
  }
}

export function getSharedEngine(key: string) {
  return CACHE[key]
}
